import {
  replaceRichTextBlock,
  richTextToPlainText,
} from "./richTextHelpers";

const LABELED_SECTIONS = ["Klinische Angaben", "Fragestellung"];

const trimBlockEdges = (block) => String(block || "").replace(/^\n+/, "").replace(/\n+$/, "");
const stripLeadingBullet = (line) => String(line || "").replace(/^\s*[•*]\s*/, "").trim();
const normalizeLabeledValue = (value) => String(value || "")
  .replace(/\r\n?/g, "\n")
  .replace(/\s*\|\s*/g, "\n")
  .split("\n")
  .map(stripLeadingBullet)
  .filter(Boolean);

const getSectionFromLine = (line) => {
  const trimmed = String(line || "").trim();
  return LABELED_SECTIONS.find((section) => trimmed.startsWith(`${section}:`)) || null;
};

const parseBefundSections = (befund) => {
  const lines = String(befund || "").replace(/\r\n?/g, "\n").split("\n");
  const sections = Object.fromEntries(LABELED_SECTIONS.map((section) => [section, []]));
  const body = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const section = getSectionFromLine(line);

    if (!section) {
      body.push(line);
      continue;
    }

    const prefix = `${section}:`;
    const inlineValue = stripLeadingBullet(String(line).trim().slice(prefix.length));
    if (inlineValue) sections[section].push(inlineValue);

    while (i + 1 < lines.length) {
      const next = lines[i + 1];
      if (getSectionFromLine(next)) break;
      i += 1;
      const normalized = stripLeadingBullet(next);
      if (normalized) sections[section].push(normalized);
    }
  }

  return { sections, body };
};

const buildBefundWithSections = (sections, bodyLines) => {
  const blockLines = [];

  LABELED_SECTIONS.forEach((section) => {
    const entries = sections[section] || [];
    if (!entries.length) return;
    if (blockLines.length) blockLines.push("");
    blockLines.push(`${section}:`);
    entries.forEach((entry) => blockLines.push(entry));
  });

  const body = [...(bodyLines || [])];
  while (body.length && body[0].trim() === "") body.shift();

  return [
    ...blockLines,
    ...(sections["Fragestellung"]?.length && body.length ? [""] : []),
    ...body,
  ].join("\n").trimEnd();
};

export const ensureLabeledLineSpacing = (befund) => {
  const parsed = parseBefundSections(befund);
  return buildBefundWithSections(parsed.sections, parsed.body);
};

const stripLeadingLabeledSections = (befund, previousValues) => {
  const lines = richTextToPlainText(befund).replace(/\r\n?/g, "\n").split("\n");
  let cursor = 0;

  while (cursor < lines.length) {
    while (cursor < lines.length && !String(lines[cursor] || "").trim()) cursor += 1;
    const section = getSectionFromLine(lines[cursor]);
    if (!section) break;

    const prefix = `${section}:`;
    const inlineValue = stripLeadingBullet(String(lines[cursor] || "").trim().slice(prefix.length));
    const expectedLines = normalizeLabeledValue(previousValues[section]);
    cursor += 1;

    const remainingExpected = inlineValue && expectedLines[0] === inlineValue
      ? expectedLines.slice(1)
      : expectedLines;
    for (const expectedLine of remainingExpected) {
      if (stripLeadingBullet(lines[cursor]) === expectedLine) cursor += 1;
    }
  }

  const body = lines.slice(cursor);
  while (body.length && !String(body[0] || "").trim()) body.shift();
  return body;
};

export const syncLabeledLine = (befund, label, oldVal, newVal, relatedValues = {}) => {
  const previousValues = { ...relatedValues, [label]: oldVal };
  const nextValues = { ...relatedValues, [label]: newVal };
  const sections = Object.fromEntries(
    LABELED_SECTIONS.map((section) => [section, normalizeLabeledValue(nextValues[section])])
  );
  const body = stripLeadingLabeledSections(befund, previousValues);
  return buildBefundWithSections(sections, body);
};

export const swapBefundBlock = (currentBefund, oldBlock, newBlock) => {
  const richResult = replaceRichTextBlock(currentBefund, oldBlock, newBlock);
  if (richResult !== null) return richResult;
  let result = currentBefund;
  const normalizedOldBlock = trimBlockEdges(oldBlock);
  const normalizedNewBlock = trimBlockEdges(newBlock);

  if (normalizedOldBlock) {
    result = result
      .replace("\n\n" + normalizedOldBlock, "")
      .replace(normalizedOldBlock + "\n\n", "")
      .replace(normalizedOldBlock, "");
  }
  if (normalizedNewBlock) {
    result = result.trimEnd() + (result.trim() ? "\n\n" : "") + normalizedNewBlock;
  }
  return result;
};
